import { Alert, Badge, Checkbox, Label } from "@xola/ui-kit";
import { SelectField } from "common";
import { useFormikContext } from "formik";
import React, { useMemo } from "react";
import { AmountType, FeeScope, FeeScopeModifier, Permission } from "types";
import { RadioInput } from "../../Common/RadioInput";
import { ErrorTooltip } from "../../Field/ErrorTooltip";
import { CurrencyInput } from "../../Input/CurrencyInput";
import { FormatInput } from "../../Input/FormatInput";
import { TaxAndFeeFormValues } from "./TaxAndFeeFormValues";
import { IsGranted } from "@/components/Common/IsGranted";
import { FilterMenuButton } from "@/components/Dashboard/Filters/FilterMenuButton";
import { FilterItem } from "@/components/Dashboard/Filters/ListFilter";
import { MultiSelectPopover } from "@/components/MultiSelectPopover";
import { useHasEquipment } from "@/hooks/useHasEquipment";
import { useModal } from "@/hooks/useVisibility";

export const TaxAndFeeTermsFields = () => {
    const popover = useModal();
    const { values, setFieldValue, errors } = useFormikContext<TaxAndFeeFormValues>();
    const hasEquipment = useHasEquipment();

    const toggleScopeModifier = (modifier: FeeScopeModifier, isAll?: boolean) => {
        const allOptions = [
            FeeScopeModifier.EXCLUDE_ADDON,
            FeeScopeModifier.EQUIPMENT_ONLY,
            FeeScopeModifier.ADDON_ONLY,
            FeeScopeModifier.PICKUP_ONLY,
            FeeScopeModifier.WARE_ONLY,
        ];

        if (isAll) {
            const isEverythingSelected =
                values.scopeModifierPerOuting.includes(FeeScopeModifier.ALL) ||
                allOptions.every((opt) => values.scopeModifierPerOuting.includes(opt));

            setFieldValue("scopeModifierPerOuting", isEverythingSelected ? [] : [FeeScopeModifier.ALL]);
            return;
        }

        let updated = [...values.scopeModifierPerOuting];

        updated = updated.filter((m) => m !== FeeScopeModifier.ALL);

        if (updated.includes(modifier)) {
            updated = updated.filter((m) => m !== modifier);
        } else {
            updated.push(modifier);
        }

        const hasAll = allOptions.every((opt) => updated.includes(opt));
        setFieldValue("scopeModifierPerOuting", hasAll ? [FeeScopeModifier.ALL] : updated);
    };

    const formatSelectedModifiers = (selected: FeeScopeModifier[]) => {
        const allOptions = [
            FeeScopeModifier.EXCLUDE_ADDON,
            FeeScopeModifier.EQUIPMENT_ONLY,
            FeeScopeModifier.ADDON_ONLY,
            FeeScopeModifier.PICKUP_ONLY,
            FeeScopeModifier.WARE_ONLY,
        ];

        if (selected.includes(FeeScopeModifier.ALL) || allOptions.every((opt) => selected.includes(opt))) {
            return "of entire purchase amount";
        }

        const labels: Record<FeeScopeModifier, string> = {
            [FeeScopeModifier.ALL]: "entire purchase",
            [FeeScopeModifier.EXCLUDE_ADDON]: "demographics",
            [FeeScopeModifier.EQUIPMENT_ONLY]: "equipment",
            [FeeScopeModifier.ADDON_ONLY]: "add-ons",
            [FeeScopeModifier.PICKUP_ONLY]: "pickup locations",
            [FeeScopeModifier.WARE_ONLY]: "merchandise",
            [FeeScopeModifier.PER_DAY]: "per day",
        };

        const filtered = selected.filter((s) => s !== FeeScopeModifier.ALL);

        if (filtered.length === 0) return "of entire purchase amount";

        if (filtered.length === 1) return `of ${labels[filtered[0]]} amount`;

        const last = labels[filtered.pop()!];
        const firstParts = filtered.map((s) => labels[s]).join(", ");
        return `of ${firstParts} and ${last} amount`;
    };

    const selected = useMemo(() => values.scopeModifierPerOuting || [], [values]);
    const buttonText = formatSelectedModifiers(selected);

    return (
        <>
            <Label className="whitespace-nowrap">Terms</Label>

            <div className="space-y-4">
                <RadioInput
                    checked={values.amountType === AmountType.ABSOLUTE}
                    onChange={() => {
                        setFieldValue("amountType", AmountType.ABSOLUTE);
                    }}
                >
                    <div
                        className="grid grid-cols-4 gap-2"
                        data-testid="tax-and-fee-terms-field-absolute"
                        onFocus={() => {
                            setFieldValue("amountType", AmountType.ABSOLUTE);
                        }}
                    >
                        <ErrorTooltip label="Amount" name="amount">
                            <div className="col-span-1">
                                <CurrencyInput
                                    className="col-span-1"
                                    name="amount"
                                    isRequired={values.amountType === AmountType.ABSOLUTE}
                                    value={values.amount}
                                    isError={!!errors.amount}
                                    onValueChange={({ floatValue }) => {
                                        setFieldValue("amount", floatValue);
                                    }}
                                />
                            </div>
                        </ErrorTooltip>
                        <div className="col-span-2 mt-0.5">
                            <SelectField name="scope">
                                <option value={FeeScope.PERSON}>Per Purchasable Demographic</option>
                                {hasEquipment && <option value={FeeScope.EQUIPMENT}>Per Purchasable Equipment</option>}
                                <option value={FeeScope.OUTING}>Per Reservation</option>
                                <option value={FeeScope.ADDON}>Per Add-on</option>
                                <IsGranted permissions={Permission.MERCHANDISE_MODIFY}>
                                    <option value={FeeScope.MERCHANDISE}>Per Merchandise</option>
                                </IsGranted>
                            </SelectField>
                        </div>

                        {values.scope === FeeScope.PERSON || values.scope === FeeScope.EQUIPMENT ? (
                            <SelectField className="col-span-1" name="scopeModifierPerPerson">
                                <option value="">Per Trip</option>
                                <option value={FeeScopeModifier.PER_DAY}>Per Day</option>
                            </SelectField>
                        ) : null}
                    </div>
                </RadioInput>

                {values.amountType === AmountType.ABSOLUTE && values.scope === FeeScope.EQUIPMENT ? (
                    <div className="col-span-1">
                        <Alert color="primary" className="py-4 px-6">
                            Tax is calculated per equipment item explicitly purchased by the customer. This tax does not
                            apply to equipment assigned to a demographic-based event.
                        </Alert>
                    </div>
                ) : null}

                <RadioInput
                    checked={values.amountType === AmountType.PERCENT}
                    onChange={() => {
                        setFieldValue("amountType", AmountType.PERCENT);
                    }}
                >
                    <div
                        className="grid grid-cols-3 gap-2"
                        data-testid="tax-and-fee-terms-field-percent"
                        onFocus={() => {
                            setFieldValue("amountType", AmountType.PERCENT);
                        }}
                    >
                        <ErrorTooltip label="Value" name="percent">
                            <FormatInput
                                isRequired={values.amountType === AmountType.PERCENT}
                                value={values.percent}
                                suffix="%"
                                isError={!!errors.percent}
                                onValueChange={({ floatValue }) => {
                                    setFieldValue("percent", floatValue);
                                }}
                            />
                        </ErrorTooltip>

                        <MultiSelectPopover
                            title=""
                            isVisible={popover.isVisible}
                            zIndex={50}
                            placement="bottom"
                            selectedCount={50}
                            target={
                                <FilterMenuButton
                                    isDropdown
                                    className="h-[39px] w-full min-w-64"
                                    text={buttonText}
                                    onClick={popover.toggle}
                                />
                            }
                            showSearch={false}
                            onApply={popover.toggle}
                            onCancel={popover.toggle}
                        >
                            <MultiSelectPopover.Item className="border-b border-gray-light bg-gray-lighter py-1.5">
                                <Checkbox
                                    checked={values.scopeModifierPerOuting.includes(FeeScopeModifier.ALL)}
                                    label={<span className="font-bold text-black">Entire Purchase amount</span>}
                                    onChange={() => toggleScopeModifier(FeeScopeModifier.ALL, true)}
                                />
                                <Badge>{`Selected ${
                                    selected.includes(FeeScopeModifier.ALL) ? 5 : selected.length
                                }`}</Badge>
                            </MultiSelectPopover.Item>

                            <MultiSelectPopover.Item className="border-b border-b-gray-light">
                                <FilterItem
                                    label="Demographic amount"
                                    isChecked={
                                        values.scopeModifierPerOuting.includes(FeeScopeModifier.EXCLUDE_ADDON) ||
                                        values.scopeModifierPerOuting.includes(FeeScopeModifier.ALL)
                                    }
                                    onChange={() => toggleScopeModifier(FeeScopeModifier.EXCLUDE_ADDON)}
                                />
                            </MultiSelectPopover.Item>

                            {hasEquipment && (
                                <MultiSelectPopover.Item className="border-b border-b-gray-light">
                                    <FilterItem
                                        label="Equipment amount"
                                        isChecked={
                                            values.scopeModifierPerOuting.includes(FeeScopeModifier.EQUIPMENT_ONLY) ||
                                            values.scopeModifierPerOuting.includes(FeeScopeModifier.ALL)
                                        }
                                        onChange={() => toggleScopeModifier(FeeScopeModifier.EQUIPMENT_ONLY)}
                                    />
                                </MultiSelectPopover.Item>
                            )}

                            <MultiSelectPopover.Item className="border-b border-b-gray-light">
                                <FilterItem
                                    label="Add-ons amount"
                                    isChecked={
                                        values.scopeModifierPerOuting.includes(FeeScopeModifier.ADDON_ONLY) ||
                                        values.scopeModifierPerOuting.includes(FeeScopeModifier.ALL)
                                    }
                                    onChange={() => toggleScopeModifier(FeeScopeModifier.ADDON_ONLY)}
                                />
                            </MultiSelectPopover.Item>

                            <MultiSelectPopover.Item className="border-b border-b-gray-light">
                                <FilterItem
                                    label="Pickup locations amount"
                                    isChecked={
                                        values.scopeModifierPerOuting.includes(FeeScopeModifier.PICKUP_ONLY) ||
                                        values.scopeModifierPerOuting.includes(FeeScopeModifier.ALL)
                                    }
                                    onChange={() => toggleScopeModifier(FeeScopeModifier.PICKUP_ONLY)}
                                />
                            </MultiSelectPopover.Item>

                            <IsGranted permissions={Permission.MERCHANDISE_MODIFY}>
                                <MultiSelectPopover.Item>
                                    <FilterItem
                                        label="Merchandise amount"
                                        isChecked={
                                            values.scopeModifierPerOuting.includes(FeeScopeModifier.WARE_ONLY) ||
                                            values.scopeModifierPerOuting.includes(FeeScopeModifier.ALL)
                                        }
                                        onChange={() => toggleScopeModifier(FeeScopeModifier.WARE_ONLY)}
                                    />
                                </MultiSelectPopover.Item>
                            </IsGranted>
                        </MultiSelectPopover>
                    </div>
                </RadioInput>
            </div>
        </>
    );
};
